// fetchResource.js
'use strict';
import fastq from 'fastq';

import fetch from '../resource/customFetch.js';
import handleFetchError from '../resource/handleFetchError.js';
import env from '../resource/env.js';

import {
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
  SET_OPERATION_DATA,
} from '../ActionTypes.js';
import getNetworkingData from '../selector/getNetworkingData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';

import { FEATURE_RESOURCE_DECRYPTION } from '../RemoteConfigKeys.js';

const operationDataSelectPath = ['queue', 'resource'];
const resourceQueue = fastq.promise(
  data =>
    // In case that the worker (such as fetch()) is just a microtask,
    // use setTimeout to make it a macrotask.
    new Promise(resolve => {
      setTimeout(() => {
        resolve(worker(data));
      }, 0);
    }),
  1
);

/**
 * Fetch resources in sequence
 * @param {string} endpoint - resource endpoint
 * @return {Promise} action promise
 */
const fetchResource = endpoint => async (dispatch, getState) => {
  resourceQueue.drain = () => {
    dispatch({
      type: SET_OPERATION_DATA,
      payload: {
        selectPath: operationDataSelectPath,
        data: {
          processing: undefined,
          waiting: [],
        },
      },
    });
  };
  return resourceQueue.push({ endpoint, dispatch, getState });
};

/**
 * Fetch a resource (it's fastq's worker)
 * @param {string} {endpoint} - The method to trigger a state change
 * @param {function} {dispatch} - The method to trigger a state change
 * @param {function} {getState} - The current state tree of web application
 * @return {Promise} Action promise.
 */
async function worker({ endpoint, dispatch, getState }) {
  if (!endpoint) {
    return dispatch({ type: '' });
  }

  dispatch({
    type: SET_OPERATION_DATA,
    payload: {
      selectPath: operationDataSelectPath,
      data: {
        processing: endpoint,
        waiting: resourceQueue.getQueue().map(element => element.endpoint),
      },
    },
  });

  const selectPath = ['resource', endpoint];

  const isAlreadyFetched = getNetworkingData(
    getState(),
    selectPath,
    'isFetched'
  );
  if (isAlreadyFetched) {
    return dispatch({ type: '' });
  }

  dispatch({ type: SET_NETWORKING_FETCHING, payload: { selectPath } });

  try {
    const isDecryptionRequired =
      getRemoteConfigData(getState(), FEATURE_RESOURCE_DECRYPTION) === 1;
    const fetchUrl = isDecryptionRequired
      ? endpoint.replace(env.PUBLIC_URL_PREFIX, env.PUBLIC_ENCRYPTED_URL_PREFIX)
      : endpoint;
    let response = await fetch(fetchUrl);

    if (!response.ok) {
      response = await handleFetchError({
        response,
        dispatch,
        getState,
        fetchUrl,
      });
    }

    await response.blob();

    return dispatch({
      type: SET_NETWORKING_SUCCESS,
      payload: { selectPath },
    });
  } catch (error) {
    return dispatch({
      type: SET_NETWORKING_ERROR,
      payload: { selectPath, error },
    });
  }
}

export default fetchResource;
